import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import useProperList from '../../../hooks/useProperList';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { listToSelectOptionAdapter } from '../../../adapters/listAdapter';
import { getDateVars } from '../../../helpers/dateHelpers';
import {
  getHoursMinutesFromMinutes,
  getStringDate,
} from '../../../adapters/dateAdapter';
import { Option } from '../../../models/formModel';
import { VisitPreSaleState } from '../../../constants/visitsConstants';
import {
  buildCalendarPcvVisitData,
  buildCalendarPcvVisitEvent,
} from '../adapters/unitPcvVisitAdapter';
import {
  getPcvVisitsEndpoint,
  getAvailableTimePcvVisitsEndpoint,
  createPcvVisitEndpoint,
  updatePcvVisitEndpoint,
  deletePcvVisitEndpoint,
  acceptPcvVisitEndpoint,
  markPcvVisitDoneEndpoint,
} from '../services/unitPcvVisitService';
import { getUnitPcvDetailEndpoint } from '../services/unitPcvService';
import {
  CreatePcvVisitForm,
  CreatePcvVisitApi,
  UpdatePcvVisitApi,
  PcvVisitList,
} from '../models/unitPcvVisit';
import { UseVisitCalendarConfig } from '../../../hooks/useVisitCalendar';
import { getLandlordByMailEndpoint } from '../../../services/landlordService';

export const useManagerUnitPcvCalendar = () => {
  const navigate = useNavigate();
  const { pcvId = '' } = useParams();
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const { propersOptions } = useProperList('for_pre_sale');

  // PCV info
  const [pcvInfo, setPcvInfo] = useState<{
    code: string;
    name: string;
    model_name: string;
    project_name: string;
  } | null>(null);

  useEffect(() => {
    if (!pcvId) return;
    callEndpoint(getUnitPcvDetailEndpoint(pcvId, token)).then(
      ({ status, data }) => {
        if (status === 200) {
          const response = fetchResponseAdapter(data);
          const { code, name, model_name, project_name } = response.data;
          setPcvInfo({ code, name, model_name, project_name });
        }
      },
    );
  }, [pcvId]);

  // Investor search
  const [landlordEmail, setLandlordEmail] = useState<string>('');
  const [landlordFound, setLandlordFound] = useState<any>(null);
  const [formError, setFormError] = useState<string>('');

  // Modals
  const [modalAdd, setModalAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [modalView, setModalView] = useState(false);
  const [modalDoneCancelled, setModalDoneCancelled] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Date / hour
  const [startDate, setStartDate] = useState('');
  const [creationStartDate, setCreationStartDate] = useState<Date>(new Date());
  const [hours, setHours] = useState<Option[]>([]);

  // Calendar event state
  const [event, setEvent] = useState<any>(null);
  const [changeEvent, setChangeEvent] = useState<any>(null);
  const [selectedProperId, setSelectedProperId] = useState<string>('');

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    unregister,
  } = useForm<CreatePcvVisitForm>({
    resolver: yupResolver(
      yup.object({
        start_time: yup.number().required('Hora de visita es requerida'),
        proper_id: yup.string().required('Proper es requerido'),
      }),
    ),
  });

  const loadHours = async (date: string) => {
    if (!date) {
      setHours([]);
      return;
    }
    const { status, data } = await callEndpoint(
      getAvailableTimePcvVisitsEndpoint(pcvId, date, token),
    );
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      setHours(
        listToSelectOptionAdapter(
          response.data,
          'minutes',
          'time_value',
          true,
          'is_blocked',
        ),
      );
    }
  };

  const handleSearchLandlord = async () => {
    if (!landlordEmail) {
      setFormError('Debes ingresar un correo valido');
      setLandlordFound(null);
      return;
    }
    setFormError('');
    const { status, data } = await callEndpoint(
      getLandlordByMailEndpoint(landlordEmail.trim().toLowerCase(), token),
    );
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      setLandlordFound(response.data);
      return;
    }
    setFormError('Inversionista no encontrado');
    setLandlordFound(null);
  };

  const closeAddEditModal = () => {
    setModalAdd(false);
    if (changeEvent !== null) changeEvent.revert();
  };

  // FullCalendar callbacks — passed to useVisitCalendar
  const fetchEvents: UseVisitCalendarConfig['fetchEvents'] = (
    start,
    end,
    successCallback,
    failureCallback,
  ) => {
    const { day: sd, month: sm, year: sy } = getDateVars(start);
    const { day: ed, month: em, year: ey } = getDateVars(end);
    callEndpoint(
      getPcvVisitsEndpoint(
        pcvId,
        `${sy}-${sm}-${sd}`,
        `${ey}-${em}-${ed}`,
        token,
      ),
    )
      .then(({ status, data }) => {
        if (status !== 200) {
          failureCallback(new Error('Error al cargar información'));
        } else {
          successCallback(
            buildCalendarPcvVisitData(fetchResponseAdapter(data).data),
          );
        }
      })
      .catch(failureCallback);
  };

  const onSelect: UseVisitCalendarConfig['onSelect'] = (info) => {
    setCreationStartDate(info.start);
    setModalAdd(true);
    setIsEdit(false);
    setStartDate(info.startStr);
    loadHours(info.startStr);
    setLandlordFound(null);
    setEvent(null);
    setChangeEvent(null);
    unregister(['start_time', 'proper_id']);
    setFormError('');
  };

  const onEventClick: UseVisitCalendarConfig['onEventClick'] = ({ event }) => {
    setEvent(event);
    setSelectedProperId('');
    if (
      event.extendedProps.status === VisitPreSaleState.DELETED ||
      event.extendedProps.status === VisitPreSaleState.DONE
    ) {
      setModalDoneCancelled(true);
    } else {
      setModalView(true);
    }
  };

  const onEventChange: UseVisitCalendarConfig['onEventChange'] = (
    eventChange,
  ) => {
    const { event: changedEvent } = eventChange;
    if (
      changedEvent.extendedProps.status === VisitPreSaleState.DELETED ||
      changedEvent.extendedProps.status === VisitPreSaleState.DONE
    ) {
      eventChange.revert();
      return;
    }
    setModalAdd(true);
    setIsEdit(true);
    setStartDate(changedEvent.startStr);
    loadHours(getStringDate(changedEvent.start));
    setLandlordFound({
      avatar: changedEvent.extendedProps.landlordInfo?.avatar,
      name: changedEvent.extendedProps.landlordInfo?.name,
      phone: '',
    });
    setValue('proper_id', changedEvent.extendedProps.proper_id);
    setEvent(changedEvent);
    setChangeEvent(eventChange);
    unregister(['start_time']);
    setFormError('');
  };

  // doCreateVisit is a factory: receives addCalendarEvent from the page (which gets it from
  // useVisitCalendar) to avoid coupling this hook to the calendar instance.
  const doCreateVisit = (addCalendarEvent: (e: any) => void) =>
    handleSubmit(async (form: CreatePcvVisitForm) => {
      setFormError('');
      if (!landlordFound?.landlord_id) {
        setFormError('Información de inversionista es requerida');
        return;
      }
      if (!startDate) {
        setFormError('No fue posible seleccionar el día para la visita');
        return;
      }
      const formApi: CreatePcvVisitApi = {
        landlord_id: landlordFound.landlord_id,
        date: startDate,
        start_time: form.start_time,
        visit_type: 'virtual',
        proper_id: form.proper_id,
      };
      const { status, data } = await callEndpoint(
        createPcvVisitEndpoint(pcvId, formApi, token),
      );
      if (status === 201) {
        const { hours: h, minutes: m } = getHoursMinutesFromMinutes(
          form.start_time,
        );
        const [yearStr, monthStr, dayStr] = startDate.split('-');
        const dateTimeVars = {
          day: parseInt(dayStr),
          month: parseInt(monthStr),
          year: parseInt(yearStr),
          hours: h,
          minutes: m,
        };
        const response = fetchResponseAdapter(data);
        const visit: PcvVisitList = {
          id: response.data.id,
          pcv_id: pcvId,
          status: VisitPreSaleState.SCHEDULED,
          visitDateTime: dateTimeVars,
          landlordInfo: {
            id: landlordFound.landlord_id,
            name: landlordFound.name,
            avatar: landlordFound.avatar,
          },
          proper_id: form.proper_id,
          proper_initial_selected_id: null,
        };
        addCalendarEvent(buildCalendarPcvVisitEvent(visit, dateTimeVars));
        setModalAdd(false);
        return;
      }
      setFormError(`(${status}) - No fue posible agendar la visita`);
    });

  const doRescheduleVisit = handleSubmit(async (form: CreatePcvVisitForm) => {
    setFormError('');
    const { id: visitId, start } = event;
    if (!visitId || !start) {
      setFormError(
        'No fue posible seleccionar el día para reagendar la visita',
      );
      return;
    }
    const formApi: UpdatePcvVisitApi = {
      date: getStringDate(start),
      start_time: form.start_time,
    };
    const { status } = await callEndpoint(
      updatePcvVisitEndpoint(pcvId, visitId, formApi, token),
    );
    if (status === 200) {
      setModalAdd(false);
      navigate(0);
      return;
    }
    setFormError(`(${status}) - No fue posible reagendar la visita`);
  });

  const handleDeleteVisit = async () => {
    if (!event) return;
    const { status } = await callEndpoint(
      deletePcvVisitEndpoint(pcvId, event.id, token),
    );
    if (status === 200) {
      setModalView(false);
      event.setExtendedProp('status', VisitPreSaleState.DELETED);
      event.setProp('classNames', 'bg-red text-white');
    }
    setDeleteConfirmOpen(false);
  };

  const handleAcceptVisit = async () => {
    if (!event) return;
    const properId = event.extendedProps.proper_id || selectedProperId;
    if (!properId) return;
    const { status } = await callEndpoint(
      acceptPcvVisitEndpoint(pcvId, event.id, properId, token),
    );
    if (status === 200) {
      event.setExtendedProp('status', VisitPreSaleState.SCHEDULED);
      event.setProp('classNames', 'bg-secondary');
      setModalView(false);
    }
  };

  const handleCompleteVisit = async () => {
    if (!event) return;
    const { status } = await callEndpoint(
      markPcvVisitDoneEndpoint(pcvId, event.id, token),
    );
    if (status === 200) {
      setModalView(false);
      event.setExtendedProp('status', VisitPreSaleState.DONE);
      event.setProp('classNames', 'bg-success text-white');
    }
  };

  return {
    // investor search
    landlordEmail,
    setLandlordEmail,
    landlordFound,
    handleSearchLandlord,
    formError,
    // modals
    modalAdd,
    isEdit,
    modalView,
    setModalView,
    modalDoneCancelled,
    setModalDoneCancelled,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    closeAddEditModal,
    // date
    startDate,
    creationStartDate,
    hours,
    // calendar callbacks (passed to useVisitCalendar)
    fetchEvents,
    onSelect,
    onEventClick,
    onEventChange,
    // selected event
    event,
    // form
    control,
    setValue,
    errors,
    doCreateVisit,
    doRescheduleVisit,
    handleDeleteVisit,
    handleAcceptVisit,
    handleCompleteVisit,
    // propers
    propersOptions,
    // pending_approval proper assignment
    selectedProperId,
    setSelectedProperId,
    // pcv info
    pcvInfo,
  };
};
